import { useCustom } from "@refinedev/core";
import { AlertTriangle, ArrowLeft, Barcode, History, QrCode, X } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router";

import { CodeSheet, type CodeRequest } from "@/components/CodeSheet";
import { ApiError } from "@/lib/api";
import {
  barcodeWord,
  boxWord,
  cardUnits,
  errorMessage,
  placeWord,
  salonCardUnits,
  salonWord,
  sortBoxesByCard,
  sortSalonsByCard,
} from "@/lib/format";
import type { Product, ProductCard, ProductSalon } from "@/lib/providers";
import { useSessionGuard } from "@/lib/useSessionGuard";

const TH =
  "sticky top-0 z-10 whitespace-nowrap border-b border-border bg-bg-elevated " +
  "px-2.5 py-2 text-[11px] font-medium uppercase tracking-[0.04em] text-fg";

/**
 * Карточка товара: размеры с баркодами и короба, в которых товар лежит.
 *
 * Два раздела отвечают на два разных вопроса склада. «Размеры» — что это за
 * вещь и какой ярлык у какого размера; «Короба» — куда за ней идти.
 *
 * Короб не привязан к площадке: он про то, что готово к отгрузке у нас, а
 * Ozon это будет или Wildberries — решится позже.
 */
export function ProductPage() {
  const { modelId = "" } = useParams();
  // Какой код показан во весь экран. `null` — ни одного: окно кода живёт
  // ровно столько, сколько его держат под сканером.
  const [code, setCode] = useState<CodeRequest | null>(null);
  // ВЫБРАННЫЙ размер — один на всю карточку, а не свой у каждого раздела.
  // Кладовщик идёт за КОНКРЕТНЫМ баркодом, и вопрос у него один: «где он
  // лежит». Разделы отвечают на него в двух местах хранения, поэтому
  // встают по одному выбору сразу оба. `null` — размер не выбран.
  const [picked, setPicked] = useState<number | null>(null);

  const productQuery = useCustom<Product>({
    url: `/api/products/${encodeURIComponent(modelId)}`,
    method: "get",
  });

  const loaded = productQuery.result.data;
  // Товаром считается только payload, в котором есть массив карточек:
  // `result.data` у Refine существует и до загрузки — пустым объектом.
  const product =
    loaded && Array.isArray(loaded.cards) && Array.isArray(loaded.boxes)
      ? loaded
      : null;
  const error = productQuery.query.error;
  useSessionGuard(error);

  // Короб называет карточки номерами, человек ищет размеры. Перевод строится
  // один раз на товар: без него в каждой строке короба стоял бы поиск по
  // массиву карточек.
  const cardOf = useMemo(() => {
    const map = new Map<number, ProductCard>();
    for (const card of product?.cards ?? []) map.set(card.card_id, card);
    return map;
  }, [product]);

  // Оба раздела встают по выбранному размеру. Пересчёт в `useMemo`, потому
  // что коробов у модели бывает под сотню, а сортировка идёт на каждый
  // перерисовка-цикл — включая открытие окна с кодом.
  const boxes = useMemo(
    () => sortBoxesByCard(product?.boxes ?? [], picked),
    [product, picked],
  );
  const salons = useMemo(
    () => sortSalonsByCard(product?.salons ?? [], picked),
    [product, picked],
  );
  // Сколько штук выбранного размера лежит в КАЖДОЙ половине склада. Числа
  // разные и складывать их можно: короб и салон дополняют друг друга.
  const pickedInBoxes = useMemo(
    () => boxes.reduce((sum, box) => sum + cardUnits(box.by_card, picked), 0),
    [boxes, picked],
  );
  const pickedInSalons = useMemo(
    () => salons.reduce((sum, salon) => sum + salonCardUnits(salon, picked), 0),
    [salons, picked],
  );

  const pickedCard = picked === null ? null : (cardOf.get(picked) ?? null);
  // Имя размера, а не его номер: «размер 54» человек читает с ярлыка, а
  // `card_id` 4172 не написан нигде. Пустой размер бывает у 890 карточек из
  // 5 165 — тогда остаётся баркод, он есть всегда.
  const pickedName = pickedCard
    ? pickedCard.size || pickedCard.barcode
    : String(picked ?? "");
  const toggle = (cardId: number) =>
    setPicked((now) => (now === cardId ? null : cardId));

  if (error) {
    // 404 — это не сбой сервиса, а «такого товара нет». Разные причины
    // требуют разных действий: одну чинит ожидание, другую — исправленный
    // номер модели.
    const missing = error instanceof ApiError && error.status === 404;
    return (
      <main
        className="grid flex-1 place-items-center p-5"
        data-component="ProductPage"
      >
        <div
          role="alert"
          className="flex max-w-[420px] flex-col items-center gap-3 rounded-[var(--radius-card)] border border-err/60 bg-err-bg p-6 text-center"
        >
          <AlertTriangle size={20} className="text-err" />
          <p className="text-[13.5px]">
            {missing
              ? `Товара с моделью ${modelId} нет в каталоге`
              : errorMessage(error)}
          </p>
          <Link
            to="/products"
            className="h-8 rounded-[var(--radius-ui)] bg-fg px-3 py-1.5 text-[13px] font-medium text-bg"
          >
            К списку товаров
          </Link>
        </div>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="flex-1 p-5" data-component="ProductPage">
        <p className="text-[13px] text-fg-muted">Загружаю…</p>
      </main>
    );
  }

  return (
    <main
      className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 md:p-5"
      data-component="ProductPage"
      data-annot="карточка товара"
    >
      <Link
        to="/products"
        className="flex w-fit items-center gap-1.5 text-[12.5px] text-fg-muted hover:text-fg"
      >
        <ArrowLeft size={13} />
        Товары
      </Link>

      <header className="flex flex-wrap items-start gap-4">
        {product.image_url ? (
          <img
            src={product.image_url}
            alt=""
            referrerPolicy="no-referrer"
            className="h-[132px] w-[96px] flex-none rounded-[var(--radius-ui)] border border-border object-cover"
            onError={(e) => {
              e.currentTarget.style.visibility = "hidden";
            }}
          />
        ) : null}
        <div className="min-w-[240px] flex-1">
          <h1 className="text-[22px] font-semibold tracking-[-0.02em]">
            {product.name || `Товар ${product.model_id}`}
          </h1>
          {/* Идентификаторы списком «подпись — значение», а не строкой через
              точки: их сверяют с ярлыком и с CRM по одному, и слипшийся ряд
              чисел для этого не годится. */}
          <dl className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[12.5px]">
            <Pair label="Модель" value={String(product.model_id)} mono />
            <Pair label="Артикул" value={product.article || "—"} mono />
            {product.model_name ? (
              <Pair label="Название модели" value={product.model_name} />
            ) : null}
            {product.supply_code ? (
              <Pair label="Код закупки" value={product.supply_code} mono />
            ) : null}
          </dl>
          {/* Поставщик и категория ведут ОБРАТНО в список, уже отобранный:
              карточку открывают вопросом «что это за вещь», а закрывают
              вопросом «а что ещё такое же у нас есть». */}
          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[12px]">
            {product.supplier.id ? (
              <Link
                to={`/products?supplier=${product.supplier.id}`}
                title={
                  product.supplier.code
                    ? `Товары поставщика ${product.supplier.name} · код ${product.supplier.code}`
                    : `Товары поставщика ${product.supplier.name}`
                }
                className="rounded border border-border px-1.5 py-0.5 text-fg-muted hover:border-border-strong hover:text-fg"
              >
                {product.supplier.name}
              </Link>
            ) : null}
            {product.category.id ? (
              <Link
                to={`/products?cat=${product.category.id}`}
                title={product.category.name}
                className="rounded border border-border px-1.5 py-0.5 text-fg-muted hover:border-border-strong hover:text-fg"
              >
                {product.category.name}
              </Link>
            ) : null}
          </div>
        </div>

        {/* Две стопки, а не четыре числа в ряд. Короб и салон — РАЗНЫЕ места
            хранения, и до 07.09.26 карточка знала только первое: 47 штук
            модели 67284, висящих в залах и в транзите, для неё не
            существовали. Подписи стопок и отвечают на вопрос «а это вообще
            про что» — раньше «Готово» отвечало на него молчанием. */}
        {/* `flex-none` здесь стоять НЕ должно: он запрещает блоку сжиматься,
            блок получает ширину по содержимому, и на телефоне плитки
            «В коробах» уезжают за край вместе со всей стопкой «В салонах»
            (проверено на 430 px). Без него блок сжимается, а плитки внутри
            переносятся — им это и разрешено. */}
        <div className="flex min-w-0 flex-wrap gap-x-5 gap-y-3">
          <Bunch title="В коробах" annot="короба">
            {/* Одно число: короба считаются единым целым — и упакованные, и
                те, что ещё набирают. Отсев делает канон короба на стороне
                сервиса (отключённые и уехавшие в поставку), а не деление
                остатка на экране. */}
            <Metric label="Штук" value={product.units} accent />
            <Metric label="Коробов" value={product.boxes.length} />
          </Bunch>
          <Bunch title="В салонах" annot="салоны">
            <Metric label="Штук" value={product.units_salon} />
            <Metric label="Салонов" value={product.salons.length} />
          </Bunch>
        </div>
      </header>

      <section>
        <h2 className="mb-2 flex flex-wrap items-baseline gap-x-2 text-[15px] font-semibold">
          Размеры и баркоды{" "}
          <span className="font-normal text-fg-muted">
            {product.cards.length} {barcodeWord(product.cards.length)}
          </span>
          {/* Подпись, а не догадка: строка таблицы, которая что-то делает по
              клику, ничем об этом не сообщает — курсор виден только тому, кто
              уже навёл.

              `basis-full` — своя строка ВСЕГДА, а не разделитель «·» перед
              текстом: на узком экране подпись и так переносится, и точка
              оставалась висеть в начале строки одна. */}
          <span className="basis-full text-[11.5px] font-normal text-fg-muted">
            {picked === null
              ? "выберите размер — короба и салоны встанут по нему"
              : "ещё раз по строке — снять выбор"}
          </span>
        </h2>
        <div className="overflow-auto rounded-[var(--radius-card)] border border-border bg-bg-subtle">
          <table className="w-full border-collapse text-[13px]">
            <caption className="sr-only">
              Баркоды товара по размерам и остаток каждого размера
            </caption>
            {/* Один ряд подписей: у размера теперь два числа остатка, и оба
                про МЕСТО ХРАНЕНИЯ — «В коробах» и «В салонах». Два ряда
                понадобились, когда коробочный остаток делился на «Готово» и
                «Прочее»; деления больше нет. */}
            <thead className="hidden md:table-header-group">
              <tr>
                <th scope="col" className={TH + " text-left"}>
                  Баркод
                </th>
                <th scope="col" className={TH + " text-left"}>
                  Размер
                </th>
                <th scope="col" className={TH + " text-left"}>
                  Цвет
                </th>
                <th scope="col" className={TH + " text-right"}>
                  В коробах
                </th>
                <th scope="col" className={TH + " text-right"}>
                  В салонах
                </th>
                <th scope="col" className={TH + " text-left"}>
                  Ozon SKU
                </th>
              </tr>
            </thead>
            <tbody className="block md:table-row-group">
              {product.cards.map((card) => (
                <tr
                  key={card.card_id}
                  data-product={card.barcode}
                  data-annot={`размер ${card.size || card.barcode}`}
                  // Строка — ПЕРЕКЛЮЧАТЕЛЬ, поэтому и роль у неё такая: без
                  // `aria-selected` выбор виден только зрячему, а склад
                  // читают в том числе с экранного диктора.
                  aria-selected={picked === card.card_id}
                  tabIndex={0}
                  onClick={() => toggle(card.card_id)}
                  onKeyDown={(e) => {
                    // Enter и пробел — то же, что клик. Строку, до которой
                    // доехали табом, иначе нельзя выбрать вовсе.
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggle(card.card_id);
                    }
                  }}
                  className={
                    "mb-2 block cursor-pointer rounded-[var(--radius-ui)] border p-2 md:mb-0 md:table-row md:rounded-none md:border-0 md:border-b md:border-border md:p-0 " +
                    (picked === card.card_id
                      ? "border-accent bg-accent-dim md:bg-accent-dim"
                      : "border-border bg-bg hover:bg-bg-hover md:bg-transparent")
                  }
                >
                  <Cell label="Баркод">
                    {/* Обёртка `flex`, потому что рядом с кодом стоит вход в
                        раздел «Баркод»: сама кнопка кода — блок во всю ширину
                        ячейки, и без обёртки ссылка переносилась бы на
                        следующую строку и висела бы под числом. */}
                    <span className="flex items-center gap-1">
                      {/* Кнопка, а не просто число: баркод существует, чтобы
                          его СКАНИРОВАЛИ, и до сих пор единственным способом
                          было набрать тринадцать цифр руками — со сканером в
                          другой руке. */}
                      <button
                        type="button"
                        onClick={(e) => {
                          // Клик по коду НЕ выбирает размер: сканирование и
                          // отбор — разные действия, и человек, поднёсший
                          // сканер, отбора не просил.
                          e.stopPropagation();
                          setCode({
                            kind: "barcode",
                            value: card.barcode,
                            title: `Баркод · размер ${card.size || "—"}`,
                          });
                        }}
                        title="Показать штрихкод Code 128"
                        data-annot="баркод"
                        className="flex items-center gap-1.5 rounded-[var(--radius-ui)] px-1 py-0.5 -mx-1 hover:bg-bg-hover"
                      >
                        <span className="tnum font-medium">{card.barcode}</span>
                        <Barcode size={14} className="flex-none text-fg-muted" />
                      </button>
                      {/* Вход в раздел «Баркод» — где ЭТОТ размер лежит и из
                          каких коробов уже уехал. Карточка товара знает только
                          «где лежит сейчас», а вопрос «когда мы оттуда брали в
                          последний раз» задают ярлыку, а не модели. */}
                      <Link
                        to={`/barcode/${card.barcode}`}
                        onClick={(e) => e.stopPropagation()}
                        title="Где лежит и где лежал этот размер"
                        data-annot="движение размера"
                        className="inline-flex flex-none items-center rounded-[var(--radius-ui)] px-1 py-0.5 text-fg-muted hover:bg-bg-hover hover:text-fg"
                      >
                        <History size={13} />
                      </Link>
                    </span>
                  </Cell>
                  <Cell label="Размер">{card.size || "—"}</Cell>
                  <Cell label="Цвет">
                    <span className="text-fg-muted">{card.color || "—"}</span>
                  </Cell>
                  <Cell label="В коробах" align="text-right tnum">
                    {card.units || <span className="text-fg-muted">0</span>}
                  </Cell>
                  <Cell label="В салонах" align="text-right tnum">
                    {/* Число ведёт в разбивку по салонам ЭТОГО размера: «10
                        штук где-то в салонах» — это не адрес, а обещание
                        обойти десять городов. */}
                    {card.units_salon ? (
                      <a
                        href="#salons"
                        onClick={(e) => {
                          // Не `toggle`: ссылка ведёт ВНИЗ, к разбору по
                          // салонам, и снять там выбор, к которому только что
                          // переехали, — значит показать разбор без него.
                          e.stopPropagation();
                          setPicked(card.card_id);
                        }}
                        title={`Где лежит размер ${card.size || "—"}`}
                        data-annot="в салонах"
                        className="font-medium underline decoration-dotted underline-offset-2 hover:text-accent-text"
                      >
                        {card.units_salon}
                      </a>
                    ) : (
                      <span className="text-fg-muted">0</span>
                    )}
                  </Cell>
                  <Cell label="Ozon SKU">
                    {/* Ноль означает «на Ozon не заведён» — так и сказано
                        словами: «0» в колонке идентификатора читается как
                        настоящий SKU, которого не существует. */}
                    {card.ozon_sku ? (
                      <span className="tnum text-fg-muted">
                        {card.ozon_sku}
                      </span>
                    ) : (
                      <span className="text-[11.5px] text-fg-muted">
                        не заведён
                      </span>
                    )}
                  </Cell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* `min-h-0` здесь стоять НЕ должно, и это не вкусовщина: карточка —
          колонка `flex` с прокруткой, а `min-h-0` разрешает сжать раздел ниже
          его содержимого. Пока раздел был последним, сжимать было нечего;
          с появлением салонов таблица коробов ужалась до 119 px при своих 702
          и легла ПОВЕРХ следующего раздела (проверено на бою 07.09.26). */}
      <section>
        <h2 className="mb-2 flex flex-wrap items-center gap-2 text-[15px] font-semibold">
          Короба{" "}
          <span className="font-normal text-fg-muted">
            {product.boxes.length} {boxWord(product.boxes.length)} ·{" "}
            {product.units} шт
          </span>
          {picked === null ? null : (
            <PickChip
              name={pickedName}
              units={pickedInBoxes}
              onClear={() => setPicked(null)}
            />
          )}
        </h2>
        {product.boxes.length === 0 ? (
          <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-6 text-center text-[13px] text-fg-muted">
            Этого товара сейчас нет ни в одном коробе.
          </div>
        ) : (
          <div className="overflow-auto rounded-[var(--radius-card)] border border-border bg-bg-subtle">
            <table className="w-full border-collapse text-[13px]">
              <caption className="sr-only">
                Короба с этим товаром: город, состояние, сколько штук и каких
                размеров
              </caption>
              <thead className="hidden md:table-header-group">
                <tr>
                  <th scope="col" className={TH + " text-left"}>
                    Короб
                  </th>
                  <th scope="col" className={TH + " text-left"}>
                    Город
                  </th>
                  <th scope="col" className={TH + " text-right"}>
                    Штук
                  </th>
                  <th scope="col" className={TH + " text-left"}>
                    Размеры внутри
                  </th>
                  <th scope="col" className={TH + " text-left"}>
                    Код места
                  </th>
                </tr>
              </thead>
              <tbody className="block md:table-row-group">
                {boxes.map((box) => (
                  <tr
                    key={box.place_id}
                    data-box={box.code}
                    // Короб без выбранного размера не исчезает, а гаснет:
                    // спрятать его значило бы соврать знаменателем — «21 из
                    // 23» перестало бы иметь смысл, а короб, куда размер
                    // докладывают, пропал бы с экрана целиком.
                    className={
                      "mb-2 block rounded-[var(--radius-ui)] border border-border bg-bg p-2 md:mb-0 md:table-row md:rounded-none md:border-0 md:border-b md:border-border md:bg-transparent md:p-0 md:hover:bg-bg-hover " +
                      (picked !== null && cardUnits(box.by_card, picked) === 0
                        ? "opacity-45"
                        : "")
                    }
                  >
                    <Cell label="Короб">
                      {/* Номер короба открывает QR с КОДОМ МЕСТА, а не с
                          самим номером: в CRM короб находят сканом места, и
                          наряд «положить в короб 10902» исполняется кодом
                          850900210902, а не числом 10902. Короб без кода
                          места остаётся простым числом — рисовать QR
                          пустоты незачем. */}
                      {box.place_barcode ? (
                        <button
                          type="button"
                          onClick={() =>
                            setCode({
                              kind: "qr",
                              value: box.place_barcode,
                              title: `Код места · короб ${box.code}`,
                            })
                          }
                          title="Показать QR с кодом места"
                          data-annot="короб"
                          className="flex items-center gap-1.5 rounded-[var(--radius-ui)] px-1 py-0.5 -mx-1 text-left hover:bg-bg-hover"
                        >
                          <BoxName box={box} />
                          <QrCode size={14} className="flex-none text-fg-muted" />
                        </button>
                      ) : (
                        <BoxName box={box} />
                      )}
                    </Cell>
                    <Cell label="Город">{box.city}</Cell>
                    {/* Два числа, а не одно. «21» без «из 23» читается как
                        «весь короб наш» и врёт кратно: на этом товаре бывает
                        4 штуки из 29. */}
                    <Cell label="Штук" align="text-right tnum">
                      <span className="font-medium">{box.units_this}</span>
                      <span className="text-fg-muted"> из {box.units_total}</span>
                    </Cell>
                    <Cell label="Размеры внутри">
                      <span className="flex flex-wrap gap-1">
                        {box.by_card.map((item) => (
                          <span
                            key={item.card_id}
                            // Выбранный размер помечен в САМОМ коробе, а не
                            // только порядком строк: короб на 11 размеров
                            // иначе заставляет перечитывать весь ряд, уже
                            // дойдя до нужной строки.
                            className={
                              "rounded border px-1.5 py-px text-[11.5px] " +
                              (item.card_id === picked
                                ? "border-accent bg-accent-dim font-medium text-accent-text"
                                : "border-border bg-bg-elevated")
                            }
                          >
                            {cardOf.get(item.card_id)?.size || item.card_id}
                            <span
                              className={
                                "tnum " +
                                (item.card_id === picked
                                  ? "text-accent-text"
                                  : "text-fg-muted")
                              }
                            >
                              {" "}
                              ×{item.units}
                            </span>
                          </span>
                        ))}
                      </span>
                    </Cell>
                    {/* Код места, а не номер: короб переносят СКАНОМ этого
                        кода, номером его зовут глазами. */}
                    <Cell label="Код места">
                      <span className="tnum text-fg-muted">
                        {box.place_barcode || "—"}
                      </span>
                    </Cell>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Salons
        salons={salons}
        units={product.units_salon}
        cardId={picked}
        sizeName={pickedName}
        pickedUnits={pickedInSalons}
        onClearCard={() => setPicked(null)}
      />

      {code ? (
        <CodeSheet request={code} onClose={() => setCode(null)} />
      ) : null}
    </main>
  );
}

/**
 * Где товар лежит ВНЕ коробов: салон, группа мест, сколько штук.
 *
 * Раздел отвечает на вопрос, которого карточка не задавала до 07.09.26.
 * «В коробах 53» звучало как весь остаток, а у модели 67284 рядом лежат ещё
 * 47 штук — в коробах FBS, в торговых залах, в транзите и в возвратах. Для
 * склада это не мелочь: половина товара была невидимой.
 *
 * Группы мест приезжают ЦЕЛИКОМ, включая «МП / Короб». Это не задвоение с
 * разделом выше: сюда попадают ровно те короба, которые каноном коробами не
 * считаются — отключённые и уехавшие в поставку. Спрятать их значило бы
 * потерять вещь между двумя разделами.
 *
 * Выбранный размер здесь СОРТИРУЕТ, а не отбирает — до 07.09.26 он отбирал.
 * Причина в вопросе, который задают экрану: «где мой размер» и «что ещё в
 * этом салоне лежит» задают его одновременно, и отбор отвечал только на
 * первую половину. Салон без выбранного размера уходит вниз и гаснет, но
 * остаётся: отправить человека в город, где рядом лежит вся остальная
 * модель, — решение, которое принимает он, а не таблица.
 */
function Salons({
  salons,
  units,
  cardId,
  sizeName,
  pickedUnits,
  onClearCard,
}: {
  salons: ProductSalon[];
  units: number;
  cardId: number | null;
  sizeName: string;
  pickedUnits: number;
  onClearCard: () => void;
}) {
  return (
    <section id="salons" data-annot="салоны">
      <h2 className="mb-2 flex flex-wrap items-center gap-2 text-[15px] font-semibold">
        В салонах{" "}
        <span className="font-normal text-fg-muted">
          {units} шт · {salons.length} {salonWord(salons.length)}
        </span>
        {cardId === null ? null : (
          <PickChip
            name={sizeName}
            units={pickedUnits}
            onClear={onClearCard}
          />
        )}
      </h2>

      {salons.length === 0 ? (
        <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-6 text-center text-[13px] text-fg-muted">
          Вне коробов этого товара в салонах нет.
        </div>
      ) : (
        <div className="overflow-auto rounded-[var(--radius-card)] border border-border bg-bg-subtle">
          <table className="w-full border-collapse text-[13px]">
            <caption className="sr-only">
              Салоны с этим товаром вне коробов: сколько штук и в каких группах
              мест они лежат
            </caption>
            <thead className="hidden md:table-header-group">
              <tr>
                <th scope="col" className={TH + " text-left"}>
                  Салон
                </th>
                <th scope="col" className={TH + " text-right"}>
                  Штук
                </th>
                <th scope="col" className={TH + " text-left"}>
                  Группы мест
                </th>
              </tr>
            </thead>
            <tbody className="block md:table-row-group">
              {salons.map((salon) => {
                const mine = salonCardUnits(salon, cardId);
                return (
                  <tr
                    key={salon.city_id}
                    data-annot={`салон ${salon.name}`}
                    className={
                      "mb-2 block rounded-[var(--radius-ui)] border border-border bg-bg p-2 md:mb-0 md:table-row md:rounded-none md:border-0 md:border-b md:border-border md:bg-transparent md:p-0 md:hover:bg-bg-hover " +
                      (cardId !== null && mine === 0 ? "opacity-45" : "")
                    }
                  >
                    <Cell label="Салон">
                      <span className="font-medium">{salon.name}</span>
                    </Cell>
                    {/* При выборе — два числа тем же способом, что у короба:
                        сколько ЗДЕСЬ выбранного размера и сколько всего.
                        Одно число «26» на выбранном размере читалось бы как
                        двадцать шесть нужных штук, а их там восемь. */}
                    <Cell label="Штук" align="text-right tnum">
                      {cardId === null ? (
                        <span className="font-medium">{salon.units}</span>
                      ) : (
                        <>
                          <span
                            className={
                              "font-medium " + (mine ? "text-accent-text" : "")
                            }
                          >
                            {mine}
                          </span>
                          <span className="text-fg-muted"> из {salon.units}</span>
                        </>
                      )}
                    </Cell>
                    <Cell label="Группы мест">
                      <span className="flex flex-wrap gap-1">
                        {salon.groups.map((group) => {
                          const here = cardUnits(group.by_card, cardId);
                          return (
                            <span
                              key={group.group_id}
                              // Число мест в подсказке, а не в строке: адрес
                              // группы — это её имя, а «две полки» уточняет,
                              // сколько их обходить, когда уже пришёл.
                              // Выбранный размер приезжает туда же: в строке
                              // ему места нет, а знать, сколько его на этой
                              // полке, нужно до того, как пошёл.
                              title={
                                `${group.places} ${placeWord(group.places)}` +
                                (here ? ` · размер ${sizeName}: ${here} шт` : "")
                              }
                              className={
                                "rounded border px-1.5 py-px text-[11.5px] " +
                                (here
                                  ? "border-accent bg-accent-dim font-medium text-accent-text"
                                  : "border-border bg-bg-elevated")
                              }
                            >
                              {group.name}
                              <span
                                className={
                                  "tnum " +
                                  (here ? "text-accent-text" : "text-fg-muted")
                                }
                              >
                                {" "}
                                ×{group.units}
                              </span>
                            </span>
                          );
                        })}
                      </span>
                    </Cell>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * Метка выбранного размера в заголовке раздела — и кнопка, снимающая выбор.
 *
 * Стоит в ОБОИХ разделах, а не один раз наверху: карточка длиннее экрана, и
 * человек, доскроллив до салонов, обязан видеть, почему они стоят в этом
 * порядке, — иначе порядок выглядит случайным.
 *
 * Число рядом с размером — сколько его в ЭТОМ разделе. Оно и отвечает на
 * вопрос «а есть ли он тут вообще»: ноль в коробах при восьми в салонах
 * говорит, куда идти, до всякого чтения таблицы.
 */
function PickChip({
  name,
  units,
  onClear,
}: {
  name: string;
  units: number;
  onClear: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClear}
      title="Снять выбор размера"
      data-annot="выбранный размер"
      className="flex items-center gap-1 rounded border border-accent bg-accent-dim px-1.5 py-0.5 text-[11.5px] font-normal text-accent-text hover:border-border-strong"
    >
      размер {name}
      <span className="tnum">· {units} шт</span>
      <X size={11} />
    </button>
  );
}

/** Номер короба и его собственный баркод — двумя строками. */
function BoxName({ box }: { box: { code: string; korob_barcode: string } }) {
  return (
    <span className="flex flex-col">
      <span className="tnum font-medium">{box.code}</span>
      {box.korob_barcode ? (
        <span className="text-[11px] text-fg-muted">{box.korob_barcode}</span>
      ) : null}
    </span>
  );
}

/**
 * Стопка чисел с общей подписью: «В коробах», «В салонах».
 *
 * Подпись здесь несущая, а не украшение. Четыре числа без неё отвечали на
 * вопрос «сколько», умалчивая о «где», — и «Готово 52» читалось как весь
 * склад, тогда как это только короба.
 */
function Bunch({
  title,
  annot,
  children,
}: {
  title: string;
  annot: string;
  children: React.ReactNode;
}) {
  return (
    <div data-annot={annot}>
      <p className="mb-1 text-[11px] uppercase tracking-[0.04em] text-fg-muted">
        {title}
      </p>
      <dl className="flex flex-wrap gap-2">{children}</dl>
    </div>
  );
}

function Pair({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-1.5">
      <dt className="text-fg-muted">{label}</dt>
      <dd className={mono ? "tnum" : ""}>{value}</dd>
    </div>
  );
}

function Metric({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: boolean;
}) {
  return (
    <div className="min-w-[92px] rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3">
      <dt className="text-[11.5px] uppercase tracking-[0.04em] text-fg-muted">
        {label}
      </dt>
      <dd
        className={
          "mt-1 text-[20px] font-semibold tabular-nums tracking-[-0.02em] " +
          (accent ? "text-accent-text" : "")
        }
      >
        {value}
      </dd>
    </div>
  );
}

/**
 * Ячейка, которая на узком экране становится строкой «подпись — значение».
 * Тот же приём, что в `PlacementTable` и в списке товаров: подпись едет из
 * `data-label` через `::before`, второго набора разметки не появляется.
 */
function Cell({
  label,
  align,
  children,
}: {
  label: string;
  align?: string;
  children: React.ReactNode;
}) {
  return (
    <td
      data-label={label}
      className={
        "flex items-center justify-between gap-3 border-b border-border/60 py-1 last:border-0 " +
        "before:text-[11px] before:uppercase before:tracking-[0.04em] before:text-fg-muted before:content-[attr(data-label)] " +
        "md:table-cell md:border-0 md:px-2.5 md:py-1.5 md:before:content-none " +
        (align ?? "")
      }
    >
      {children}
    </td>
  );
}
